import { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { customStyles } from '@/data/toast/toastConfig';
import { TextInput } from '@/components/input/TextInput';
import { toast } from 'react-toastify';
import { Button } from '@/components/button/Button';
import { Loader } from '@/components/loader/Loader';
import { supportedChains } from '@bonadocs/core';
import { getLatestBlock } from '@/utils/latestBlock';
import { getCachedBlockNumber, cacheBlockNumber } from '@bonadocs/zimulatoor';

interface BonadocsEditorViewActionsCodeModalProps {
  className?: string;
  show?: boolean;
  chainId: number | string;
  closeBlockModal: () => void;
}

export const BonadocsEditorViewActionsCodeModal: React.FC<
  BonadocsEditorViewActionsCodeModalProps
> = ({ show, closeBlockModal, chainId }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshLoading, setRefreshLoading] = useState<boolean>(false);
  const [blockNumber, setBlockNumber] = useState<string | number>('');
  const [open, isOpen] = useState<boolean>(false);

  const closeModal = () => {
    isOpen(!open);
    closeBlockModal();
  };

  const currentChain = Array.from(supportedChains).filter((instance) => {
    if (Number(chainId) === instance[0]) {
      return {
        label: instance[1].name,
        value: instance[1].chainId,
      };
    }
  });

  const updateCachedBlockNumber = async () => {
    getCachedBlockNumber(BigInt(chainId)).then(async (blockNumber) => {
   
      if (!blockNumber) {
        const rpcUrl = currentChain[0]?.[1].jsonRpcUrl;

        const latestBlockNumber = await getLatestBlock(rpcUrl);
        setBlockNumber(Number(latestBlockNumber));
      } else {
        setBlockNumber(Number(blockNumber));
      }
    });
  };

  useEffect(() => {
    isOpen(show ?? false);
  }, [show]);

  useEffect(() => {
    if (chainId) updateCachedBlockNumber();
  }, [chainId]);

  return (
    // @ts-ignore
    <Modal
      style={customStyles}
      contentLabel="Contract Modal"
      isOpen={open}
      onRequestClose={closeModal}
    >
      <div className="modal__close" onClick={closeModal}>
        <svg
          className="modal__close__img"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6 6L18 18"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div className="modal__container">
        <h3 className="modal__container__title">Set Block number</h3>
        <div className="modal__container__text">
          {currentChain.length > 0
            ? `${currentChain[0]?.[1].name} Block number`
            : 'Block number'}
        </div>

        <TextInput
          placeholder="block number"
          type="number"
          handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setBlockNumber(event.target.value);
          }}
          value={blockNumber}
        />

        <div className="modal__container__wrapper">
          <Button
            type="inertia"
            onClick={async () => {
              setRefreshLoading(true);
              try {
                const rpcUrl = currentChain[0]?.[1].jsonRpcUrl;

                const latestBlockNumber = await getLatestBlock(rpcUrl);

                await cacheBlockNumber(
                  BigInt(chainId),
                  BigInt(latestBlockNumber),
                  Date.now() + 3600000,
                );
                setBlockNumber(latestBlockNumber.toString());

                setRefreshLoading(false);
                closeBlockModal();
              } catch {
                setLoading(false);
                toast.error('Error creating updating block number');
              }
            }}
            className="modal__container__button"
          >
            <>
              {refreshLoading ? (
                <Loader className="spinner" />
              ) : (
                'Use Latest Block'
              )}
            </>
          </Button>
          <Button
            disabled={
              loading || String(blockNumber).length === 0 || refreshLoading
            }
            type="action"
            onClick={async () => {
              setLoading(true);
              try {
                await cacheBlockNumber(
                  BigInt(chainId),
                  BigInt(blockNumber),
                  Date.now() + 3600000,
                );

                setLoading(false);
                closeBlockModal();
              } catch {
                setLoading(false);
                toast.error('Error creating updating block number');
              }
            }}
            className="modal__container__button ma-l"
          >
            <>{loading ? <Loader className="spinner" /> : 'Set Block Number'}</>
          </Button>
        </div>
      </div>
    </Modal>
  );
};
